import * as React from 'react';
import { Button } from 'antd';
import { MAPBOX_API_KEY } from '../../utils/ClientsideApiKeys';
import { debounce } from '../../utils/PerformanceUtils';

import mapboxgl from 'mapbox-gl';
import { ErrorBoundary } from '@sentry/react';
import { createRoot, Root } from 'react-dom/client';
import MapGalleryMarkerPopover, { MapGalleryMarkerPopupPayload } from './MapGalleryMarkerPopover';
import './MapboxGallery.less';
const isEqual = require('fast-deep-equal');

const CENTER_OF_UNITED_STATES = [-98.5795, 39.8283];

class MapboxGalleryComponent extends React.Component<any, any> {
	map: any;
	mapContainer: any;
	markerRoots: Root[];
	// @ts-ignore
	markers: mapboxgl.Marker[];

	activeMarkerId: number | null;
	markerPopupPayloads: MapGalleryMarkerPopupPayload[];

	state = {
		showSearchWithinArea: false,
		bounds: {},
		center: CENTER_OF_UNITED_STATES,
	};

	constructor(props) {
		super(props);
		this.markerRoots = [];
		this.markers = [];
		this.activeMarkerId = null;
		this.markerPopupPayloads = [];
	}

	clearMarkersAndRoots() {
		if (this.markers && this.markers.length) {
			this.markers.forEach((marker) => {
				marker.remove();
			});
		}
		if (this.markerRoots && this.markerRoots.length) {
			this.markerRoots.forEach((root) => {
				root.unmount();
			});
		}
		this.markers = [];
		this.markerRoots = [];
	}

	handleMarkerOpenChange = (markerIndex: number, open: boolean) => {
		this.activeMarkerId = open ? markerIndex : null;
		this.rerenderMarkerPopovers();
	};

	rerenderMarkerPopovers() {
		this.markerRoots.forEach((root, index) => {
			const payload = this.markerPopupPayloads[index];
			if (payload === undefined) return;
			root.render(
				<MapGalleryMarkerPopover
					popup={payload}
					getPopupContainer={() => this.mapContainer}
					isOpen={this.activeMarkerId === index}
					onOpenChange={(open) => this.handleMarkerOpenChange(index, open)}
				/>
			);
		});
	}

	buildMarkers(geolocatedEntities, popupRenderFunc, longitudeAccessor, latitudeAccessor) {
		if (!this.map || !this.mapContainer) {
			return;
		}
		this.clearMarkersAndRoots();
		this.activeMarkerId = null;
		this.markerPopupPayloads = geolocatedEntities.map((gle) => popupRenderFunc(gle));
		this.markers = geolocatedEntities.map((gle, index) => {
			const mountEl = document.createElement('div');
			const root = createRoot(mountEl);
			root.render(
				<MapGalleryMarkerPopover
					popup={this.markerPopupPayloads[index]}
					getPopupContainer={() => this.mapContainer}
					isOpen={false}
					onOpenChange={(open) => this.handleMarkerOpenChange(index, open)}
				/>
			);
			this.markerRoots.push(root);
			const marker = new mapboxgl.Marker({
				element: mountEl,
				anchor: 'bottom',
			})
				.setLngLat([longitudeAccessor(gle), latitudeAccessor(gle)])
				.addTo(this.map);
			return marker;
		});
	}

	debouncedMoveEndCallback = debounce(
		(bounds) => {
			if (!this.props.hideSearchWithinArea) {
				this.setState({ showSearchWithinArea: true, bounds });
			}
			this.updateCenter();
		},
		250,
		false
	);

	handleSearchWithinArea = () => {
		const { bounds, center } = this.state;
		this.props.handleSearchAgain(bounds, center);
		this.setState({ showSearchWithinArea: false });
	};

	updateCenter = () => {
		if (this.map) {
			const center = this.map.getCenter();
			this.setState({ center: [center.lng, center.lat] });
		}
	};

	componentDidMount() {
		const {
			startingLongitude = CENTER_OF_UNITED_STATES[0],
			startingLatitude = CENTER_OF_UNITED_STATES[1],
			startingZoomLevel = 2,
			geolocatedEntities,
			popupRenderFunc = (gle) =>
				`<div style="width: 25px; height: 25px; border-radius: 50%;"><img width=100%" height="auto" src="${gle.logoURL}" /></div><div>${gle.displayName}</div>`,
			longitudeAccessor = (el) => el.longitude,
			latitudeAccessor = (el) => el.latitude,
			showNavControls,
			baseLayerStyle,
		} = this.props;
		// tslint-disable-next-line
		mapboxgl.accessToken = MAPBOX_API_KEY;
		this.map = new mapboxgl.Map({
			container: this.mapContainer,
			style: `mapbox://styles/mapbox/${baseLayerStyle}-v9`,
			center: [startingLongitude, startingLatitude],
			zoom: startingZoomLevel,
			attributionControl: false,
		});
		this.map.on('moveend', () => {
			this.debouncedMoveEndCallback(this.map.getBounds());
		});
		if (showNavControls) {
			this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }));
		}

		this.buildMarkers(geolocatedEntities, popupRenderFunc, longitudeAccessor, latitudeAccessor);

		const filteredGeolocatedEntities = geolocatedEntities.filter(
			(gle) => longitudeAccessor(gle) && latitudeAccessor(gle)
		);
		if (filteredGeolocatedEntities.length > 0) {
			const minLongitude = Math.min.apply(null, filteredGeolocatedEntities.map(longitudeAccessor));
			const maxLongitude = Math.max.apply(null, filteredGeolocatedEntities.map(longitudeAccessor));
			const minLatitude = Math.min.apply(null, filteredGeolocatedEntities.map(latitudeAccessor));
			const maxLatitude = Math.max.apply(null, filteredGeolocatedEntities.map(latitudeAccessor));
			this.map.fitBounds(
				[
					[minLongitude, minLatitude],
					[maxLongitude, maxLatitude],
				],
				{ padding: 40 }
			);
		}
	}

	componentDidUpdate(prevProps) {
		const {
			geolocatedEntities,
			popupRenderFunc = (gle) =>
				`<div style="width: 25px; height: 25px; border-radius: 50%;"><img width=100%" height="auto" src="${gle.logoURL}" /></div><div>${gle.displayName}</div>`,
			longitudeAccessor = (el) => el.longitude,
			latitudeAccessor = (el) => el.latitude,
		} = this.props;
		if (!isEqual(geolocatedEntities, prevProps.geolocatedEntities) && this.map) {
			this.buildMarkers(geolocatedEntities, popupRenderFunc, longitudeAccessor, latitudeAccessor);
			const filteredGeolocatedEntities = geolocatedEntities.filter(
				(gle) => longitudeAccessor(gle) && latitudeAccessor(gle)
			);
			if (filteredGeolocatedEntities.length > 0) {
				const minLongitude = Math.min.apply(
					null,
					filteredGeolocatedEntities.map(longitudeAccessor)
				);
				const maxLongitude = Math.max.apply(
					null,
					filteredGeolocatedEntities.map(longitudeAccessor)
				);
				const minLatitude = Math.min.apply(null, filteredGeolocatedEntities.map(latitudeAccessor));
				const maxLatitude = Math.max.apply(null, filteredGeolocatedEntities.map(latitudeAccessor));
				this.map.fitBounds(
					[
						[minLongitude, minLatitude],
						[maxLongitude, maxLatitude],
					],
					{
						padding: 40,
					}
				);
			}
		}
	}

	componentWillUnmount() {
		this.clearMarkersAndRoots();
		if (this.map) {
			this.map.remove();
		}
	}

	render() {
		const { showSearchWithinArea } = this.state;

		const style = {
			position: 'absolute' as 'absolute',
			top: 0,
			bottom: 0,
			left: 0,
			right: 0,
			width: '100%',
		};

		return (
			<div className="mapbox-gallery" style={style} ref={(el) => (this.mapContainer = el)}>
				{showSearchWithinArea ? (
					<Button
						style={{
							position: 'absolute',
							left: '50%',
							top: '8px',
							transform: 'translateX(-50%)',
							zIndex: 2,
						}}
						onClick={this.handleSearchWithinArea}
					>
						Redo search in area
					</Button>
				) : null}
			</div>
		);
	}
}

export default class MapboxGallery extends React.Component<any, any> {
	render(): React.ReactNode {
		return (
			<ErrorBoundary fallback={null}>
				<MapboxGalleryComponent {...this.props} />
			</ErrorBoundary>
		);
	}
}
